import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { BookOpen } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Only these 6 canonical monthly 2026 slugs appear in the public archive
const PUBLIC_ARCHIVE_SLUGS = [
  'january-2026',
  'february-2026',
  'march-2026',
  'april-2026',
  'may-2026',
  'june-2026',
];

const fmtFullMeta = (issueDate, vol, num) => {
  const v = vol || 6;
  const n = String(num).padStart(2, '0');
  const volPart = `VOL. ${v} NO. ${n}`;
  const datePart = issueDate ? issueDate.toUpperCase() : '';
  return datePart ? `${datePart} · ${volPart}` : volPart;
};

const Spinner = () => (
  <div style={{ minHeight: '40vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <div style={{ textAlign: 'center' }}>
      <div style={{ width: 36, height: 36, border: '3px solid var(--op-teal)', borderTopColor: 'transparent', borderRadius: '50%', animation: 'arc-spin 0.8s linear infinite', margin: '0 auto 12px' }} />
      <p style={{ color: 'var(--op-muted)', fontSize: '0.875rem' }}>Loading issues…</p>
    </div>
  </div>
);

const ArchiveIssueCard = ({ issue }) => (
  <div
    className="op-arc-card"
    style={{
      background: 'var(--op-paper-2)',
      border: '1px solid var(--op-border)',
      borderRadius: 16,
      overflow: 'hidden',
      boxShadow: '0 2px 14px rgba(8,29,48,.06)',
      display: 'flex',
      flexDirection: 'column',
    }}
    data-testid="archive-issue-card"
  >
    {/* Cover */}
    <div style={{ position: 'relative', height: 290, overflow: 'hidden', background: 'linear-gradient(160deg,#081D30 0%,#102F43 55%,#006E6A 100%)', flexShrink: 0 }}>
      {issue.cover_image_url ? (
        <img
          src={issue.cover_image_url}
          alt={issue.title}
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', transition: 'transform 0.4s ease' }}
          className="op-arc-cover-img"
          data-testid="archive-card-cover"
        />
      ) : (
        <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
          <div style={{ width: 52, height: 52, borderRadius: 10, background: 'rgba(255,253,248,.12)', border: '2px solid rgba(255,253,248,.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#FFFDF8', fontWeight: 900, fontSize: 18 }}>OP</div>
          <p style={{ color: '#FFFDF8', fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', textAlign: 'center', padding: '0 16px' }}>
            {issue.issue_date || 'COVER COMING'}
          </p>
        </div>
      )}
      {/* Gradient overlay */}
      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top,rgba(8,29,48,.48) 0%,transparent 52%)', pointerEvents: 'none' }} />
    </div>

    {/* Content */}
    <div style={{ padding: '1.15rem 1.35rem 1.4rem', display: 'flex', flexDirection: 'column', flex: 1 }}>
      <p style={{ fontSize: '0.62rem', fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--op-teal)', marginBottom: '0.45rem' }} data-testid="archive-card-meta">
        {issue.issue_number
          ? fmtFullMeta(issue.issue_date, issue.volume_number, issue.issue_number)
          : issue.issue_date}
      </p>
      <h3
        style={{ fontSize: '1rem', fontWeight: 800, color: 'var(--op-ink)', letterSpacing: '-0.025em', lineHeight: 1.3, marginBottom: '1rem', flex: 1 }}
        data-testid="archive-card-title"
      >
        {issue.title}
      </h3>
      <Link
        to={`/magazine/${issue.slug}`}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          background: 'var(--op-navy)', color: '#fff',
          padding: '0.52rem 1.1rem', borderRadius: 8,
          fontSize: '0.8rem', fontWeight: 700, textDecoration: 'none',
          transition: 'background 0.2s, transform 0.2s',
          alignSelf: 'flex-start',
        }}
        data-testid="archive-card-read-btn"
      >
        <BookOpen size={13} /> Read Online
      </Link>
    </div>
  </div>
);

export default function MagazineArchivePage() {
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/magazine`)
      .then(r => {
        const filtered = r.data
          .filter(i => PUBLIC_ARCHIVE_SLUGS.includes(i.slug))
          .sort((a, b) => (a.issue_number || 0) - (b.issue_number || 0));
        setIssues(filtered);
      })
      .catch(e => console.error('Failed to load magazine issues:', e))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div style={{ background: 'var(--op-paper)', minHeight: '80vh' }} data-testid="magazine-archive-page">
      <style>{`
        @keyframes arc-spin { to { transform: rotate(360deg); } }
        .op-arc-card { transition: box-shadow 0.25s ease, transform 0.25s ease; }
        .op-arc-card:hover { box-shadow: 0 14px 44px rgba(8,29,48,.13) !important; transform: translateY(-3px); }
        .op-arc-card:hover .op-arc-cover-img { transform: scale(1.04); }
        @media (max-width: 900px) { .op-arc-grid { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; } }
        @media (max-width: 560px) { .op-arc-grid { grid-template-columns: 1fr !important; gap: 1.25rem !important; } }
      `}</style>

      {/* Page header */}
      <div style={{ borderBottom: '1px solid var(--op-border)', background: 'var(--op-paper-2)' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', padding: '2.8rem 1.5rem 2.4rem' }}>
          <Link
            to="/magazine"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: 'var(--op-teal)', fontSize: '0.78rem', fontWeight: 700, textDecoration: 'none', marginBottom: '1.5rem', letterSpacing: '-0.01em' }}
            data-testid="archive-back-to-magazine"
          >
            ← Back to Magazine
          </Link>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: '0.8rem' }}>
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              background: 'rgba(14,107,101,.08)', border: '1px solid rgba(14,107,101,.15)',
              borderRadius: 999, padding: '0.3rem 0.8rem',
              fontSize: '0.62rem', fontWeight: 900, letterSpacing: '0.08em', textTransform: 'uppercase',
              color: 'var(--op-teal)',
            }}>
              2026 Monthly Issues
            </span>
          </div>

          <h1 style={{
            fontFamily: "'Instrument Serif', Georgia, serif",
            fontSize: 'clamp(1.9rem, 4vw, 2.9rem)',
            fontWeight: 900,
            color: 'var(--op-navy)',
            letterSpacing: '-0.045em',
            lineHeight: 1.05,
            marginBottom: '0.65rem',
          }}>
            The Magazine Archive
          </h1>
          <p style={{ color: 'var(--op-muted)', fontSize: '0.95rem', maxWidth: '52ch', lineHeight: 1.65 }}>
            Every issue of Opportunity Post, January–June 2026. Free to read online.
          </p>
        </div>
      </div>

      {/* Issue grid */}
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '3rem 1.5rem 4rem' }}>
        {loading ? (
          <Spinner />
        ) : issues.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '4rem 0' }}>
            <p style={{ color: 'var(--op-muted)', fontSize: '1rem' }}>No issues available.</p>
            <Link to="/magazine" style={{ color: 'var(--op-teal)', fontWeight: 700, fontSize: '0.85rem', marginTop: 12, display: 'inline-block' }}>← Back to Magazine</Link>
          </div>
        ) : (
          <>
            <p style={{ color: 'var(--op-muted)', fontSize: '0.8rem', fontWeight: 700, marginBottom: '1.75rem', letterSpacing: '0.02em' }}>
              {issues.length} issue{issues.length !== 1 ? 's' : ''} available
            </p>
            <div
              className="op-arc-grid"
              style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '2rem' }}
              data-testid="archive-grid"
            >
              {issues.map(issue => (
                <ArchiveIssueCard key={issue.id} issue={issue} />
              ))}
            </div>
          </>
        )}
      </div>

      {/* Footer strip */}
      <div style={{ borderTop: '1px solid var(--op-border)', background: 'var(--op-paper-2)', padding: '2rem 1.5rem' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
          <p style={{ color: 'var(--op-muted)', fontSize: '0.78rem', fontWeight: 700 }}>
            Opportunity Post Magazine &bull; Vol. 6, 2026
          </p>
          <Link
            to="/magazine"
            style={{ color: 'var(--op-teal)', fontSize: '0.78rem', fontWeight: 700, textDecoration: 'none' }}
            data-testid="archive-footer-back"
          >
            ← Back to Magazine
          </Link>
        </div>
      </div>
    </div>
  );
}
